import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Row, Col } from 'react-bootstrap';

export const CuratedTables = ({
    fetchData,
    tableRefresh,
    chosenFolderId,
    committedSearch,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
}) => {

    const fetchDataOptions = useMemo(
        () => ({
            pageIndex,
            pageSize,
            chosenFolderId,
            committedSearch,
        }),
        [pageIndex, pageSize, chosenFolderId, committedSearch]
    );

    const dataQuery = useQuery({
        queryKey: ['candidate-table', fetchDataOptions, committedSearch, tableRefresh],
        queryFn: () => fetchData(fetchDataOptions),
        keepPreviousData: true,
    });

    const table = useReactTable({
        data: dataQuery.data?.rows?.data ?? [],
        columns: dataQuery.data?.rows?.columns ?? [],
        pageCount: dataQuery.data?.pageCount ?? -1,

        state: {
            pagination: { pageIndex, pageSize },
        },

        onPaginationChange: (updater) => {
            const next = typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater;

            if (next.pageIndex !== pageIndex) setPageIndex(next.pageIndex);
            if (next.pageSize !== pageSize) setPageSize(next.pageSize);
        },

        manualPagination: true,
        getCoreRowModel: getCoreRowModel(),
    });

    const renderPageNumbers = () => {
        const totalPageCount = table.getPageCount();
        const currentPage = table.getState().pagination.pageIndex + 1;
        const pageNumbers = [];

        pageNumbers.push(1);

        if (totalPageCount > 5) {
            if (currentPage > 3) pageNumbers.push('...');
            const start = Math.max(2, currentPage - 1);
            const end = Math.min(totalPageCount - 1, currentPage + 1);
            for (let i = start; i <= end; i++) pageNumbers.push(i);
            if (currentPage < totalPageCount - 2) pageNumbers.push('...');
            pageNumbers.push(totalPageCount);
        } else {
            for (let i = 2; i <= totalPageCount; i++) pageNumbers.push(i);
        }

        return pageNumbers;
    };

    return (
        <div>
            <div className="table-wrap curated-table-wrap">
                <table className="tables curated-table">
                    <thead>
                        {table.getHeaderGroups().map((hg) => (
                            <tr key={hg.id}>
                                {hg.headers.map((header) => (
                                    <th key={header.id}>
                                        {!header.isPlaceholder &&
                                            flexRender(header.column.columnDef.header, header.getContext())}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>

                    <tbody>
                        {table.getRowModel().rows.map((row) => (
                            <tr key={row.id}>
                                {row.getVisibleCells().map((cell) => (
                                    <td key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="pagination-controls mt-3">
                <Row className="align-items-center g-2 flex-nowrap">

                    <Col xs="auto">
                        <div className="pagination-container d-flex gap-1">

                            <button type="button" className="border rounded p-1"
                                onClick={() => table.setPageIndex(0)}
                                disabled={!table.getCanPreviousPage()}>
                                {'<<'}
                            </button>

                            <button type="button" className="border rounded p-1"
                                onClick={() => table.previousPage()}
                                disabled={!table.getCanPreviousPage()}>
                                {'<'}
                            </button>

                            {renderPageNumbers().map((page, idx) => (
                                <button
                                    type="button"
                                    key={idx}
                                    className={`border rounded p-1 ${pageIndex + 1 === page ? 'active' : ''}`}
                                    onClick={() => typeof page === 'number' && table.setPageIndex(page - 1)}
                                    disabled={typeof page !== 'number'}
                                >
                                    {page}
                                </button>
                            ))}

                            <button type="button" className="border rounded p-1"
                                onClick={() => table.nextPage()}
                                disabled={!table.getCanNextPage()}>
                                {'>'}
                            </button>

                            <button type="button" className="border rounded p-1"
                                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                                disabled={!table.getCanNextPage()}>
                                {'>>'}
                            </button>

                        </div>
                    </Col>

                    {/* Go to page */}
                    <Col xs="auto">
                        <div className="d-flex align-items-center gap-2">
                            <span>Go to page:</span>
                            <input
                                type="number"
                                defaultValue={pageIndex + 1}
                                onChange={(e) => {
                                    const page = Number(e.target.value);
                                    table.setPageIndex(Number.isNaN(page) ? 0 : page - 1);
                                }}
                                className="border p-1 rounded"
                                style={{ width: '3rem' }}
                            />
                        </div>
                    </Col>

                    {/* Page size */}
                    <Col xs="auto">
                        <div className="d-flex align-items-center gap-2">
                            <span>Show</span>
                            <select
                                value={pageSize}
                                onChange={(e) => setPageSize(Number(e.target.value))}
                                className="border p-1 rounded">
                                {[10, 20, 30, 40, 50].map((size) => (
                                    <option key={size} value={size}>{size}</option>
                                ))}
                            </select>
                        </div>
                    </Col>

                </Row>
            </div>
        </div>
    );
};